import { FC, RefObject, useCallback, useContext, useEffect, CSSProperties, useRef, useState } from 'react';
import { useResizeEffect, ChipConnected, PassiveComponentContext, useAudioTrigger, useModifiers } from '@pp/core';

import { Bets } from '../../../config/bets';
import { BetSpotNames } from '../../../store/slices/mwState/types';
import { MWTimings } from '../../../config/MWTimings';
import styles from './BetSpot.module.css';

export interface BetSpotProps {
  value: number;
  onBet: (bet: Bets) => void;
  bet: Bets;
  megaMultiplier: number;
  disabled?: boolean;
  multiplier: number;
  winning?: boolean;
  betSpotRef?: RefObject<HTMLDivElement>;
  chipRef?: RefObject<HTMLDivElement>;
  isDraggedFrom?: boolean;
  isDragging?: boolean;
  highlight?: boolean;
  isBetsOpenStatus?: boolean;
  isLoadedInActiveRound?: boolean;
  result?: object | null;
  isMegaWin: boolean;
  clickSound?: string;
  isMax?: boolean;
}

const resizeObserver = new ResizeObserver((entries) => {
  entries.forEach((entry) => {
    const target = entry.target as HTMLDivElement;
    target.style.setProperty('--scale', `${target.clientWidth / 113}`);
  });
});
const resetAnimation = (element: RefObject<HTMLDivElement>): void => {
  if (!element.current) return;
  element.current.style.animationPlayState = 'paused';
  void element.current.offsetHeight; // NOSONAR
  element.current.style.animationPlayState = 'running';
};
const stopAnimation = (element: RefObject<HTMLDivElement>): void => {
  if (!element.current) return;
  element.current.style.animation = 'none';
};
export const BetSpot: FC<BetSpotProps> = ({
  value,
  onBet,
  bet,
  disabled,
  multiplier,
  megaMultiplier,
  betSpotRef,
  chipRef,
  isDraggedFrom,
  isDragging,
  winning,
  result,
  isMegaWin,
  clickSound,
  isBetsOpenStatus,
  isMax,
}) => {
  const fallingChipRef = useRef<HTMLDivElement>(null);
  const fallbackRef = useRef<HTMLDivElement>(null);
  const megaMultiplierRef = useRef<HTMLDivElement>(null);
  const onPress = useRef(false);
  const wrapper = betSpotRef || fallbackRef;
  const [previousValue, setPreviousValue] = useState<number>(0);
  const [multiplierGlow, setMultiplierGlow] = useState(false);
  const audioTrigger = useAudioTrigger();
  const isPassive = useContext(PassiveComponentContext);

  const playSound = useCallback(
    (clickSound: string) => {
      audioTrigger.play({ name: clickSound });
    },
    [audioTrigger, bet]
  );

  const dropChip = useCallback(() => {
    if (isPassive) return;
    if (wrapper.current && !onPress.current) {
      wrapper.current.classList.remove(styles.betspot__click_Animation);
      void wrapper.current.offsetWidth; // NOSONAR
      wrapper.current.classList.add(styles.betspot__click_Animation);
    }
    onPress.current = false;
  }, [isPassive, wrapper]);

  useEffect(() => {
    if (wrapper.current) {
      const wrapperElement = wrapper.current;
      resizeObserver.observe(wrapperElement);

      return () => {
        resizeObserver.unobserve(wrapperElement);
      };
    } else {
      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [wrapper]);

  useEffect(() => {
    if (value && !disabled) {
      setPreviousValue(value);
      if (previousValue < value) {
        dropChip();
      }
    } else {
      setPreviousValue(0);
    }
  }, [value, previousValue, setPreviousValue, dropChip, disabled]);

  useEffect(() => {
    if (isPassive) return;
    if (megaMultiplier > 0) {
      setMultiplierGlow(true);

      let glowTimerId: NodeJS.Timeout | undefined = setTimeout(() => {
        setMultiplierGlow(false);
      }, MWTimings.multiplierAnimationDuration);
      audioTrigger.play({ name: `megaMultiplierReveal` });

      return () => {
        audioTrigger.stop({ name: `megaMultiplierReveal` });
        clearTimeout(glowTimerId);
      };
    }
  }, [megaMultiplier, isPassive]);

  useEffect(() => {
    if (fallingChipRef.current && winning) {
      fallingChipRef.current.classList.remove(styles.megaMultiplier__chip_animating);
      fallingChipRef.current.classList.add(styles.megaMultiplier__chip_animating);
    }
  }, [winning, fallingChipRef, value]);

  useResizeEffect(
    useCallback(() => {
      if (megaMultiplierRef.current) {
        const playstate = window.getComputedStyle(megaMultiplierRef.current).animationPlayState;
        if (playstate === 'running') {
          resetAnimation(megaMultiplierRef);
        } else {
          stopAnimation(megaMultiplierRef);
        }
      }
    }, [])
  );
  const handleRelease = (isBetsOpenStatus: any): void => {
    if (disabled) return;
    if (isBetsOpenStatus && !isDragging) {
      onBet(bet);
      if (clickSound != null) {
        playSound(clickSound);
      }
      onPress.current = true;
      if (wrapper.current) {
        wrapper.current.classList.remove(styles.betspot__click_Animation);
      }
      setTimeout(() => {
        window.navigator.vibrate?.([50]);
      }, 1);
    }
  };

  const mods = useModifiers(styles, 'betSpot', {
    disabled: disabled && !value && !multiplierGlow && !winning,
    isBetspotClose: !isBetsOpenStatus,
    hasChip: !!value,
    isDraggedFrom,
    winning,
    megaMultiplierValue: megaMultiplier > 0,
    result: !!result,
    isMax: isMax,
    isPassive: isPassive,
    isMegaWin: isMegaWin,
  });

  return (
    <div
      style={{ '--betSpotColor': `var(--game-traffic-light-mw_${BetSpotNames[bet]})` } as CSSProperties}
      data-test-id={`betspot_${BetSpotNames[bet]}`}
      onPointerUp={handleRelease}
      className={`${styles.betSpot} ${mods}`}
      ref={wrapper}
    >
      {megaMultiplier > 0 && (
        <>
          <div className={styles.betSpot__megaMultiplierValue__parent}>
            <div className={styles.betSpot__megaMultiplierValue} ref={megaMultiplierRef}>
              <span data-test-id={`multiplier_${BetSpotNames[bet]}`}>{`${megaMultiplier}x`}</span>
            </div>
          </div>
          <div className={styles.glow}></div>
        </>
      )}
      {winning && <div className={`${styles.winner_glow} `}></div>}
      <div className={`${styles.betSpot__body} ${isPassive ? styles.betSpot__body_help : ''}`}>
        <div className={styles.betSpot__background} />
        <div className={styles.betSpot__chipContainer}>
          {value > 0 && (
            <>
              <div className={styles.betSpot__baseChip} ref={chipRef}>
                <ChipConnected size="grow" value={value} />
              </div>
              <div className={styles.betSpot__chip} ref={fallingChipRef}>
                <ChipConnected size="grow" value={value} />
              </div>
            </>
          )}
        </div>
        <div className={styles.betSpot__contentBackground} />
        <div className={styles.betSpot__content}>
          <div className={styles.betSpot__valueContainer}>
            <div className={styles.betSpot__value}>{BetSpotNames[bet]}</div>
          </div>
        </div>
        {!megaMultiplier && (
          <div className={styles.betSpot__multiplier}>
            <div className={styles.betSpot__multiplierBackground} />
            <div className={styles.betSpot__multiplierValue}>
              <span data-test-id={`multiplier_${BetSpotNames[bet]}`}>{multiplier}x</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
